import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";

export default function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  
  // Get organization from URL parameters
  const [searchParams] = useSearchParams();
  const organization_id = searchParams.get('organization');

  const { loginWithRedirect, logout, isAuthenticated, user } = useAuth0();
  
  // Handle successful authentication when user returns from Auth0
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log("User authenticated, calling handleSuccessfulLogin");
      handleSuccessfulLogin(user);
    }
  }, [isAuthenticated, user, navigate,]);
  
  // Store user data and set onboarding status
  function handleSuccessfulLogin(userData) {
    // If this is a new user or first login, set hasCompletedOnboarding to false
    const userInfo = {
      ...userData,
      // For new users, they haven't completed onboarding
      hasCompletedOnboarding: userData.hasCompletedOnboarding || false
    };
    
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    console.log('User info saved to localStorage', userInfo);
    
    // Let AuthCallback handle the proper redirect based on organization
    // Don't hardcode redirect here
    console.log('User info saved, AuthCallback will handle redirect');
  }
  
  const handleSignIn = async () => {
    try {
      if (loginWithRedirect) {
        const authParams = {
          authorizationParams: {
            redirect_uri: `${window.location.origin}/callback`,
          }
        };

        if (organization_id) {
          authParams.authorizationParams.organization = organization_id;
        }

        console.log("Sign-in parameters:", authParams);

        await loginWithRedirect(authParams);
      } else {
        console.error('Auth0 not properly initialized');
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleSignOut = async () => {
    try {
      if (logout) {
        await logout({
          logoutParams: {
            returnTo: window.location.origin 
          }
        });
        console.log('User signed out');
      } else {
        console.error('Auth0 logout method not available');
      }
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const handleGoogleSignIn = () => {
    console.log('Google sign-in clicked');
  };

  // Removed unused Google sign-out helper to satisfy lint rules

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col px-4">
      {/* Brand Header */}
      <div className="w-full py-6 px-4 md:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-900 hover:text-blue-600 transition-colors">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4.75L19.25 9L12 13.25L4.75 9L12 4.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.25 11.5L4.75 14L12 18.25L19.25 14L14.75 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="text-xl font-bold">ArcAttend</span>
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center">
        {/* Logo and Title */}
        <div className="mb-8 text-center">
          <div className="w-12 h-12 bg-blue-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4.75L19.25 9L12 13.25L4.75 9L12 4.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.25 11.5L4.75 14L12 18.25L19.25 14L14.75 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Sign in to your account</h2>
        </div>

        {/* Sign In Form */}
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-lg px-8 py-8 space-y-6 border border-gray-100">
            <form className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>
                <a href="/forgot-password" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                  Forgot password?
                </a>
              </div>

              <button
                type="button"
                onClick={handleSignIn}
                className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors"
              >
                Sign Out
              </button>
            </form>

            {/* Social Sign In */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
              </svg>
              <span className="ml-2">Google</span>
            </button>
          </div>
        </div>
        

        {/* Sign Up Link */}
        <p className="mt-8 text-center text-sm text-gray-500">
          Not a member?{' '}
          <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}